import React from 'react';
import { Link } from 'react-router-dom';
import FreePro from './FreeProGuide';
import EventJourneyGuide from './EventJourneyGuide';
import EditCancelGuide from './EditCancelGuide';
import { CheckIn, CreateEvent, HostChat, JoinEvent, ManageParticipants } from './GuideEvents';
import ContextualMedia from '../../components/ContextualMedia';
import {
  BulletList,
  Card,
  GuideMediaComposition,
  IllustratedSteps,
  NumSteps,
  Prereqs,
  RelatedLinks,
  ResultNote,
  Shot,
  useGuideTheme,
} from './guideBlocks';
import { CAPTURE_BASES } from '../../lib/selectedCaptures';

const AccountProfile: React.FC = () => (
  <>
    <Prereqs items={['RidePulse installed', 'An email address']} />
    <IllustratedSteps
      steps={[
        { title: 'Create your account', description: 'Sign up with your email address and a password.', shot: { base: 'Auth/iOS - Auth - Create Account', alt: 'Create account screen in RidePulse', caption: 'Create account' } },
        { title: 'Build your profile', description: 'Add a photo, display name, riding style, and experience level so other riders know who they\'re riding with.', shot: { base: CAPTURE_BASES.profile, alt: 'A completed rider profile in RidePulse', caption: 'A filled-out rider profile' } },
        { title: 'Set your preferences', description: 'Choose your pace preferences and privacy defaults. You can change all of this later.' },
      ]}
    />
    <ResultNote>Done. Your profile is visible to other riders at the privacy level you choose.</ResultNote>
    <RelatedLinks links={[
      { id: 'add-motorcycle', label: 'Add a motorcycle' },
      { id: 'profile-preferences', label: 'Profile & preferences' },
    ]} />
  </>
);

const AddMotorcycle: React.FC = () => {
  const { dsColors, heroSecondary } = useGuideTheme();
  return (
    <>
      <Prereqs items={['An account & profile (see Set Up Your Account)']} />
      <div className="max-w-xl">
        <NumSteps
          steps={[
            { title: 'Choose your motorcycle', description: 'Open Garage and choose Add Motorcycle. Select the make, model, model year and paint color, plus a variation when offered.' },
            { title: 'Add a photo', description: 'Use the photo control to add a picture of your motorcycle.' },
            { title: 'Add optional details', description: 'Enter a nickname, license plate and current mileage if you want to include them. Paint customization and card styling are separate from mechanical specifications.' },
            { title: 'Save to your Garage', description: 'Review the details and save. Your motorcycle can then be selected for an event.' },
          ]}
        />
        <p className="mt-6 text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
          To change motorcycle visibility, open Profile, then Privacy. In Garage Privacy, choose a default or turn on Customize privacy per motorcycle. These settings control your public profile; the bike you bring remains visible to that event&rsquo;s participants.
        </p>
        <p className="mt-3 text-sm">
          <Link
            to="/help#profile-preferences"
            className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
            style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
          >
            Profile and privacy settings →
          </Link>
        </p>
      </div>
      <ResultNote>Your bike appears in your garage and can be selected when you join events.</ResultNote>
      <RelatedLinks links={[
        { id: 'account-profile', label: 'Account & profile' },
        { id: 'join-event', label: 'Find & join' },
      ]} />
    </>
  );
};

const ProfilePreferences: React.FC = () => {
  const { heroPrimary, heroSecondary } = useGuideTheme();
  return (
    <>
      <GuideMediaComposition
        context={
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Privacy Controls', description: 'Choose who sees your profile, garage, and activity. Control location sharing and visibility settings.' },
              { title: 'Garage Settings', description: 'Add multiple bikes, set privacy per motorcycle, and choose which bike to display as primary.' },
            ].map((c) => (
              <Card key={c.title}>
                <h3 className="text-lg font-bold mb-2" style={{ color: heroPrimary }}>{c.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>{c.description}</p>
              </Card>
            ))}
          </div>
        }
        shot={{ base: CAPTURE_BASES.privacyDefaults, alt: 'Privacy Options in RidePulse showing event participation visibility and default motorcycle privacy', caption: 'Event and Garage defaults.' }}
        following={
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Riding Preferences', description: 'Set your experience level, riding style, and pace preferences to match with compatible riders.' },
              { title: 'Notification Preferences', description: 'Choose what alerts you receive for events, messages, and updates. Set quiet hours if needed.' },
            ].map((c) => (
              <Card key={c.title}>
                <h3 className="text-lg font-bold mb-2" style={{ color: heroPrimary }}>{c.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>{c.description}</p>
              </Card>
            ))}
          </div>
        }
      />
      <RelatedLinks links={[
        { id: 'add-motorcycle', label: 'Add a motorcycle' },
        { id: 'ride-safely', label: 'Ride safely' },
      ]} />
    </>
  );
};

const GroupPulseGuide: React.FC = () => {
  const { heroSecondary } = useGuideTheme();
  return (
    <>
      <Prereqs items={['Joining or hosting an event']} />
      <div className="max-w-xl">
        <p className="text-base leading-relaxed mb-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>
          GroupPulse is opt-in for every participant. Enable it when you join an event, and the host sees shared rider locations and statuses on a single live map.
        </p>
        <NumSteps
          steps={[
            { title: 'Opt in when joining', description: 'Enable GroupPulse tracking in the join flow; it\u2019s off unless you turn it on.' },
            { title: 'Ride', description: 'Your location and status update for the host during the ride. Your profile privacy settings stay exactly as you set them.' },
            { title: 'Or stay private', description: '"Location not shared" is shown to the host if you choose not to share.' },
          ]}
        />
        <ResultNote>SOS is the one exception: sending an SOS shares your location with the host and other participants in the event, even if you chose not to share.</ResultNote>
      </div>
      <RelatedLinks links={[
        { id: 'join-event', label: 'Find & join' },
        { id: 'ride-safely', label: 'Ride safely' },
      ]} />
    </>
  );
};

const RideSafely: React.FC = () => {
  const { heroPrimary } = useGuideTheme();
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-bold mb-4" style={{ color: heroPrimary }}>Before Every Ride</h3>
          <BulletList items={['Check your motorcycle', 'Wear proper safety gear', 'Check the weather', 'Tell someone where you\u2019re going', 'Bring your phone and charger']} />
        </Card>
        <Card>
          <h3 className="text-lg font-bold mb-4" style={{ color: heroPrimary }}>Emergency Features</h3>
          <BulletList items={['Add emergency contacts', 'Opt-in to GroupPulse tracking', 'Hosts can see emergency contacts', 'Always call 911 for emergencies']} />
        </Card>
      </div>
      <RelatedLinks links={[
        { id: 'grouppulse', label: 'GroupPulse' },
        { id: 'join-event', label: 'Find & join' },
      ]} />
    </>
  );
};

const Achievements: React.FC = () => {
  const { heroPrimary, heroSecondary } = useGuideTheme();
  return (
    <>
      <Prereqs items={['Attend or host events']} />
      <ContextualMedia
        media={
          <Shot shot={{ base: 'Sample Data/iOS - Sample Data - Achievements', alt: 'Achievements screen with badges and progress in RidePulse', caption: 'Achievements' }} />
        }
      >
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {[
            { title: 'Automatic Tracking', description: 'Achievements are checked after every event you attend or host. Progress tracked automatically.' },
            { title: 'Instant Rewards', description: 'Earn badges immediately when requirements are met. Get notified and add them to your profile.' },
            { title: 'Track Progress', description: 'View progress bars on locked achievements. See how close you are to your next milestone.' },
          ].map((m) => (
            <Card key={m.title}>
              <h3 className="text-lg font-bold mb-2" style={{ color: heroPrimary }}>{m.title}</h3>
              <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{m.description}</p>
            </Card>
          ))}
        </ul>
        <p className="text-sm font-semibold" style={{ color: heroSecondary }}>
          Must check in to events (not just RSVP) • Event must be completed • No manual claiming needed
        </p>
      </ContextualMedia>
      <RelatedLinks links={[
        { id: 'profile-preferences', label: 'Profile & preferences' },
        { id: 'join-event', label: 'Find & join' },
      ]} />
    </>
  );
};

const BlockingReporting: React.FC = () => {
  const { heroPrimary, heroSecondary } = useGuideTheme();
  return (
    <>
      <GuideMediaComposition
        context={
          <Card>
            <h3 className="text-lg font-bold mb-2" style={{ color: heroPrimary }}>Block a rider</h3>
            <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>From a rider’s profile, open the menu and choose Block. This ends the connection: no friendship and no pending request. While blocked, they cannot see your profile or send you friend requests. Manage your list from Friends → Blocked Users, where you can unblock anytime.</p>
          </Card>
        }
        shot={{ base: 'snapshots/20261004T200401Z/Social/iOS - Social - Blocked Users', alt: 'RidePulse Blocked Users list showing one blocked sample rider and an Unblock control', caption: 'Blocked users.' }}
        following={
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <h3 className="text-lg font-bold mb-2" style={{ color: heroPrimary }}>Report a rider</h3>
              <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>From a rider’s profile, open the menu and choose Report, then pick a reason. Reports go to moderation for review. Report conduct that breaks the rules; block when you simply do not want the connection.</p>
            </Card>
            <Card>
              <h3 className="text-lg font-bold mb-2" style={{ color: heroPrimary }}>Host controls</h3>
              <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>Hosts run their own rosters. A host can remove a participant, and a removed rider cannot rejoin that event. Hosts can also remove messages and mute participants in their event chat.</p>
            </Card>
          </div>
        }
      />
      <ResultNote>Blocking ends the relationship. It does not remove either of you from a shared event, and event chat stays visible to everyone in that event.</ResultNote>
      <RelatedLinks links={[
        { id: 'manage-participants', label: 'Manage participants' },
        { id: 'ride-safely', label: 'Ride safely' },
      ]} />
    </>
  );
};

export const GUIDE_BODIES: Record<string, React.FC> = {
  'event-journey': EventJourneyGuide,
  'account-profile': AccountProfile,
  'add-motorcycle': AddMotorcycle,
  'profile-preferences': ProfilePreferences,
  'join-event': JoinEvent,
  'create-event': CreateEvent,
  'edit-cancel': EditCancelGuide,
  'manage-participants': ManageParticipants,
  'check-in': CheckIn,
  'host-chat': HostChat,
  'grouppulse': GroupPulseGuide,
  'ride-safely': RideSafely,
  'achievements': Achievements,
  'blocking-reporting': BlockingReporting,
  'free-pro': FreePro,
};
